import type { BreadcrumbProps } from '@blueprintjs/core'
import { observer } from 'mobx-react-lite'
import React, { useMemo } from 'react'
import { RenderASIN } from '../../columns'
import {
    activeAdGroupFilter,
    activeCampaignFilter,
    adGroupFilter,
    campaignFilter,
    PerformanceFilters,
    TableName,
} from '../../config'
import { useKdpFilters } from '../../hooks/useKdpFilters'
import { useUser } from '../../hooks/useUser'
import type { TableProps } from '../ad_groups/AdGroupsTable'
import { addExtraCols } from '../campaigns/CampaignsTable'
import { Layout } from '../Layout'
import {
    type CellProps,
    Columns,
    defaultPerfColumns,
    type ExpandedPerformanceMetrics,
    extra,
} from '../table2/ad_grid_columns'
import { type AgGridColumnDef, AgGridTable, DummyHeader } from '../table2/AgGridTable'
export {
    campaignFilter,
    activeCampaignFilter,
    adGroupFilter,
    activeAdGroupFilter,
} from '../../config'

const defaultFilters = [
    campaignFilter,
    activeCampaignFilter,
    adGroupFilter,
    activeAdGroupFilter,
    ...PerformanceFilters,
]

export type Product = any

export const ProductsTable = observer(
    ({
        nested,
        store,
        savedFiltersStore,
        tableHooks,
    }: TableProps<Product & ExpandedPerformanceMetrics>): JSX.Element => {
        const useUserValues = useUser()
        const filters = useKdpFilters(defaultFilters)

        const breadcrumbs: BreadcrumbProps[] = [{ text: 'Manage' }]

        const cols3 = useMemo(() => {
            return newColumns(useUserValues.is_kdp)
        }, [useUserValues.is_kdp])

        return (
            <Layout
                breadcrumbs={breadcrumbs}
                primary={TableName.Product}
                nested={nested}
                params={tableHooks.params}
            >
                <AgGridTable
                    name={TableName.Product}
                    columns={cols3}
                    tableHooks={tableHooks}
                    store={store}
                    showHeader={false}
                    savedFiltersStore={savedFiltersStore}
                    filters={filters}
                    search
                    searchPlaceholder="Search by ASIN"
                    nested={nested}
                    columnSelection
                />
            </Layout>
        )
    },
)

const newColumns = <T,>(isKdp: boolean): AgGridColumnDef<T>[] => {
    const cols: AgGridColumnDef<T>[] = [
        ASIN,
        Columns.TotalCampaigns,
        Columns.EnabledCampaigns,
        Columns.TotalAdGroups,
        Columns.EnabledAdGroups,
        ...defaultPerfColumns,
    ]

    addExtraCols(cols, isKdp)

    return cols.filter(
        (col) =>
            col.field !== 'ideal_acos' && col.field !== 'ideal_cpc' && col.field !== 'target_acos',
    )
}

const ASINCell = ({ data, context }: CellProps<Product & ExpandedPerformanceMetrics>) => {
    return <RenderASIN {...data} {...extra(context)} popover={false} />
}

const ASIN: AgGridColumnDef<Product & ExpandedPerformanceMetrics> = {
    field: 'asin',
    headerName: 'ASIN',
    headerComponent: DummyHeader,
    cellRenderer: ASINCell,
    width: 125,
}
